import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import ReservationModal from '../../pages/reservations/components/ReservationModal.jsx';
import { authService } from '../../services/api';
import { useUser } from '../../hooks/useUser';

const Sidebar = ({ isOpen, onClose, onCollapseChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  
  // Refs para melhor performance
  const sidebarRef = useRef(null);
  const mobileSidebarRef = useRef(null);
  
  // Estados principais
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Sanitização de dados para segurança
  const sanitizeInput = useCallback((input) => {
    if (typeof input !== 'string') return '';
    return input.replace(/[<>]/g, '').trim();
  }, []);

  // Validação de dados de navegação
  const validateNavigationItem = useCallback((item) => {
    if (!item || typeof item !== 'object') return false;
    return item.label && item.path && item.icon && typeof item.label === 'string';
  }, []);

  // Memoized navigation items com validação e sanitização
  const navigationItems = useMemo(() => {
    const items = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard',
        description: 'Visão geral operacional',
        badge: null,
        disabled: false
    },
    {
      label: 'Reservas',
      path: '/reservations',
      icon: 'Calendar',
        description: 'Gestão de reservas',
        badge: null,
        disabled: false
    },
    {
      label: 'Quartos',
      path: '/room-management',
      icon: 'Bed',
        description: 'Gestão de quartos',
        badge: null,
        disabled: false
    },
    {
      label: 'Financeiro',
      path: '/financial-management',
      icon: 'DollarSign',
        description: 'Gestão financeira',
        badge: null,
        disabled: false
    },
    {
      label: 'Relatórios',
      path: '/reports-and-analytics',
      icon: 'BarChart3',
        description: 'Relatórios e análises',
        badge: null,
        disabled: false
      },
    {
      label: 'Pesquisas',
      path: '/surveys',
      icon: 'MessageSquare',
        description: 'Avaliações e feedback',
        badge: null,
        disabled: false
      }
    ];

    return items.filter(validateNavigationItem).map(item => ({
      ...item,
      label: sanitizeInput(item.label),
      description: sanitizeInput(item.description)
    }));
  }, [validateNavigationItem, sanitizeInput]);

  const settingsItems = useMemo(() => {
    const items = [
    {
      label: 'Configurações',
      path: '/settings',
      icon: 'Settings',
        description: 'Configurações gerais do sistema',
        badge: null,
        disabled: false
      }
    ];

    return items.filter(validateNavigationItem).map(item => ({
      ...item,
      label: sanitizeInput(item.label),
      description: sanitizeInput(item.description)
    }));
  }, [validateNavigationItem, sanitizeInput]);

  const quickActions = useMemo(() => [
    {
      label: 'Check-in Rápido',
      icon: 'UserCheck',
      action: () => handleQuickCheckIn(),
      disabled: false,
      description: 'Processar check-in de hóspedes'
    },
    {
      label: 'Nova Reserva',
      icon: 'Plus',
      action: () => setShowReservationModal(true),
      disabled: false,
      description: 'Criar nova reserva'
    },
    {
      label: 'Relatório Diário',
      icon: 'FileText',
      action: () => handleDailyReport(),
      disabled: false,
      description: 'Gerar relatório do dia'
    }
  ], []);

  // Funções otimizadas com useCallback
  const isActiveRoute = useCallback((path) => {
    return location.pathname === path;
  }, [location.pathname]);

  const handleQuickCheckIn = useCallback(() => {
    try {
      setIsLoading(true);
      // Implementar lógica de check-in rápido
      console.log('Quick check-in initiated');
      // Simular operação
      setTimeout(() => {
        setIsLoading(false);
        console.log('Quick check-in completed');
      }, 1000);
    } catch (error) {
      console.error('Erro no check-in rápido:', error);
      setIsLoading(false);
    }
  }, []);

  const handleDailyReport = useCallback(() => {
    try {
      setIsLoading(true);
      // Implementar lógica de relatório diário
      console.log('Daily report generation initiated');
      // Simular operação
      setTimeout(() => {
        setIsLoading(false);
        console.log('Daily report completed');
      }, 1500);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      setIsLoading(false);
    }
  }, []);

  const handleSaveReservation = useCallback(async (reservationData) => {
    try {
      setIsLoading(true);
      console.log('Reserva criada:', reservationData);
      setShowReservationModal(false);
      
      // Simular salvamento
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Recarregar a página para atualizar os dados
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      console.error('Erro ao criar reserva:', error);
      alert('Erro ao criar reserva. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleNavigation = useCallback((path) => {
    if (typeof path === 'string' && path.startsWith('/')) {
      navigate(path);
      if (onClose) onClose();
    }
  }, [navigate, onClose]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  // Fechar sidebar mobile quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && mobileSidebarRef.current && !mobileSidebarRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Prevenir scroll do body quando sidebar mobile estiver aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Notificar mudanças no estado de colapso
  useEffect(() => {
    if (onCollapseChange) {
      onCollapseChange(isCollapsed);
    }
  }, [isCollapsed, onCollapseChange]);


  return (
    <>
      {/* Desktop Sidebar com melhor UX e acessibilidade */}
      <aside 
        ref={sidebarRef}
        className="fixed left-0 top-16 bottom-0 bg-card border-r border-border z-50 backdrop-blur-sm bg-card/95 hidden lg:block"
        role="navigation"
        aria-label="Menu de navegação principal"
        style={{ 
          width: isCollapsed ? '4rem' : '15rem',
          transition: 'width 0.3s ease-in-out',
          backgroundColor: 'var(--color-card)',
          borderRight: '1px solid var(--color-border)',
          zIndex: 50
        }}
      >
        <div className="flex flex-col h-full">
          {/* Collapse Toggle com melhor UX */}
          <div className="p-4 border-b border-border relative z-10">
            <button
              onClick={toggleCollapse}
              type="button"
              className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-muted hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 relative z-20 cursor-pointer"
              style={{
                pointerEvents: 'auto',
                zIndex: 20,
                position: 'relative'
              }}
              aria-label={isCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
              title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
            >
              <Icon 
                name={isCollapsed ? "ChevronRight" : "ChevronLeft"} 
                size={20} 
                color="var(--color-muted-foreground)" 
                className="transition-transform duration-200"
              />
            </button>
          </div>

          {/* Navigation com melhor acessibilidade */}
          <nav className="flex-1 p-4" role="navigation" aria-label="Menu principal">
            <div className="space-y-2">
              {navigationItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  disabled={item.disabled || isLoading}
                  className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-2 rounded-lg transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                    isActiveRoute(item.path)
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted hover:scale-[1.02]'
                  } ${item.disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={isCollapsed ? item.label : ''}
                  aria-label={`Navegar para ${item.label}`}
                  aria-current={isActiveRoute(item.path) ? 'page' : undefined}
                >
                  <Icon 
                    name={item.icon} 
                    size={20} 
                    color={isActiveRoute(item.path) ? 'currentColor' : 'var(--color-muted-foreground)'} 
                    className="flex-shrink-0"
                  />
                  {!isCollapsed && (
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium">{item.label}</div>
                      <div className="text-xs opacity-75">{item.description}</div>
                    </div>
                  )}
                  {item.badge && !isCollapsed && (
                    <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Settings Items integrados na navegação principal */}
            <div className="space-y-2">
                {settingsItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    disabled={item.disabled || isLoading}
                    className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-2 rounded-lg transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                      isActiveRoute(item.path)
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted hover:scale-[1.02]'
                    } ${item.disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={isCollapsed ? item.label : ''}
                    aria-label={`Navegar para ${item.label}`}
                    aria-current={isActiveRoute(item.path) ? 'page' : undefined}
                  >
                    <Icon 
                      name={item.icon} 
                      size={20} 
                      color={isActiveRoute(item.path) ? 'currentColor' : 'var(--color-muted-foreground)'} 
                      className="flex-shrink-0"
                    />
                    {!isCollapsed && (
                      <div className="flex-1 text-left">
                        <div className="text-sm font-medium">{item.label}</div>
                        <div className="text-xs opacity-75">{item.description}</div>
                      </div>
                    )}
                    {item.badge && !isCollapsed && (
                      <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
            </div>
          </nav>

          {/* Quick Actions com melhor UX */}
          {!isCollapsed && (
            <div className="p-4 border-t border-border">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center">
                <Icon name="Zap" size={12} className="mr-2" />
                Ações Rápidas
              </h3>
              <div className="space-y-2">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={action.action}
                    disabled={action.disabled || isLoading}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted hover:scale-[1.02] transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                      action.disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    title={action.description}
                    aria-label={action.description}
                  >
                    <Icon 
                      name={action.icon} 
                      size={16} 
                      color="var(--color-muted-foreground)" 
                      className="flex-shrink-0"
                    />
                    <span className="text-sm">{action.label}</span>
                    {isLoading && (
                      <div className="ml-auto">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Collapsed Quick Actions com melhor UX */}
          {isCollapsed && (
            <div className="p-4 border-t border-border">
              <div className="space-y-2">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={action.action}
                    disabled={action.disabled || isLoading}
                    className={`w-full flex items-center justify-center p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                      action.disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    title={action.label}
                    aria-label={action.description}
                  >
                    <Icon 
                      name={action.icon} 
                      size={16} 
                      color="var(--color-muted-foreground)" 
                    />
                    {isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Sidebar Overlay com melhor UX */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 lg:hidden animate-in fade-in-0 duration-300"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile Sidebar com melhor UX e acessibilidade */}
      <aside 
        ref={mobileSidebarRef}
        className={`fixed left-0 top-0 bottom-0 bg-card border-r border-border z-50 transition-transform duration-300 w-64 lg:hidden backdrop-blur-sm bg-card/95 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        role="navigation"
        aria-label="Menu de navegação mobile"
        aria-hidden={!isOpen}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Header com melhor UX */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center shadow-sm">
                <Icon name="Hotel" size={20} color="white" />
              </div>
              <span className="font-semibold text-foreground">Menu</span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Fechar menu"
            >
              <Icon name="X" size={20} color="var(--color-muted-foreground)" />
            </button>
          </div>

          {/* Navigation Mobile com melhor UX */}
          <nav className="flex-1 p-4 overflow-y-auto" role="navigation" aria-label="Menu principal mobile">
            <div className="space-y-2">
              {navigationItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  disabled={item.disabled || isLoading}
                  className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                    isActiveRoute(item.path)
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted hover:scale-[1.02]'
                  } ${item.disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label={`Navegar para ${item.label}`}
                  aria-current={isActiveRoute(item.path) ? 'page' : undefined}
                >
                  <Icon 
                    name={item.icon} 
                    size={20} 
                    color={isActiveRoute(item.path) ? 'currentColor' : 'var(--color-muted-foreground)'} 
                    className="flex-shrink-0"
                  />
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium">{item.label}</div>
                    <div className="text-xs opacity-75">{item.description}</div>
                  </div>
                  {item.badge && (
                    <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Settings Section Mobile com melhor UX */}
            <div className="mt-6">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-3 flex items-center">
                <Icon name="Settings" size={12} className="mr-2" />
                Configurações
              </h3>
              <div className="space-y-2">
                {settingsItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    disabled={item.disabled || isLoading}
                    className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                      isActiveRoute(item.path)
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted hover:scale-[1.02]'
                    } ${item.disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    aria-label={`Navegar para ${item.label}`}
                    aria-current={isActiveRoute(item.path) ? 'page' : undefined}
                  >
                    <Icon 
                      name={item.icon} 
                      size={20} 
                      color={isActiveRoute(item.path) ? 'currentColor' : 'var(--color-muted-foreground)'} 
                      className="flex-shrink-0"
                    />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium">{item.label}</div>
                      <div className="text-xs opacity-75">{item.description}</div>
                    </div>
                    {item.badge && (
                      <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </nav>

          {/* Quick Actions Mobile com melhor UX */}
          <div className="p-4 border-t border-border">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center">
              <Icon name="Zap" size={12} className="mr-2" />
              Ações Rápidas
            </h3>
            <div className="space-y-2">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => {
                    action.action();
                    if (action.label !== 'Nova Reserva') {
                      onClose();
                    }
                  }}
                  disabled={action.disabled || isLoading}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted hover:scale-[1.02] transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                    action.disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title={action.description}
                  aria-label={action.description}
                >
                  <Icon 
                    name={action.icon} 
                    size={18} 
                    color="var(--color-muted-foreground)" 
                    className="flex-shrink-0"
                  />
                  <span className="text-sm">{action.label}</span>
                  {isLoading && (
                    <div className="ml-auto">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </aside>

      {/* Reservation Modal */}
      <ReservationModal
        isOpen={showReservationModal}
        onClose={() => setShowReservationModal(false)}
        reservation={null}
        onSave={handleSaveReservation}
        mode="create"
      />
    </>
  );
};

export default Sidebar;